import { useEffect, useRef, useState } from 'react';
import { tripsApi } from '../../../api/trips.api';

export type BackgroundKind = 'photo' | 'upload' | 'gradient';

export interface LoadedBackground {
  image: ImageBitmap | null;
  width: number;
  height: number;
}

const EMPTY: LoadedBackground = { image: null, width: 0, height: 0 };

/** Only a gallery photo is fetched; an uploaded file is decoded straight from
 *  the user's disk and a gradient has nothing to fetch. */
function cacheKey(kind: BackgroundKind, photoId: string | null): string | null {
  return kind === 'photo' && photoId ? `photo:${photoId}` : null;
}

/**
 * The chosen background, decoded and ready for the canvas. Bytes come from
 * `GET /trips/:id/share-background` — same-origin, so `toBlob()` on the export
 * canvas doesn't throw a tainted-canvas error — and already resized to the
 * story box.
 *
 * Everything the caller reads is derived from the decoded set, not mirrored
 * into state: switching back to a background already fetched is then a plain
 * render, with no flash of the empty one in between.
 */
export function useStickerBackground(tripId: string, kind: BackgroundKind, photoId: string | null) {
  const [decoded, setDecoded] = useState<Record<string, LoadedBackground>>({});
  const [failedKeys, setFailedKeys] = useState<string[]>([]);
  const key = cacheKey(kind, photoId);

  useEffect(() => {
    if (!key || decoded[key] || failedKeys.includes(key)) return;

    let cancelled = false;
    tripsApi.shareBackground(tripId, photoId!)
      .then(blob => createImageBitmap(blob))
      .then(image => {
        if (cancelled) {
          image.close();
          return;
        }
        setDecoded(current => ({ ...current, [key]: { image, width: image.width, height: image.height } }));
      })
      .catch(() => { if (!cancelled) setFailedKeys(current => [...current, key]); });

    return () => { cancelled = true; };
  }, [tripId, kind, photoId, key, decoded, failedKeys]);

  // A decoded story bitmap is ~8 MB, so a browsing session through the gallery
  // is worth releasing explicitly rather than leaving to the collector.
  const latest = useRef(decoded);
  useEffect(() => { latest.current = decoded; }, [decoded]);
  useEffect(() => () => Object.values(latest.current).forEach(entry => entry.image?.close()), []);

  return {
    background: (key && decoded[key]) || EMPTY,
    loading: Boolean(key) && !decoded[key!] && !failedKeys.includes(key!),
    failed: Boolean(key) && failedKeys.includes(key!),
  };
}
